/**
 * Import Manager Dashboard - Provider Data Imports
 * Redesigned with Tailwind + Lucide to match CBO Import Manager
 *
 * Features:
 * - Provider imports from CMS NPPES, HRSA, etc.
 * - AI enrichment for providers and CBOs
 * - Web scraping for additional data
 * - Social media review aggregation
 */

import React, { useState, useEffect } from 'react';
import {
  Database,
  Download,
  Activity,
  RefreshCw,
  Play,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  TrendingUp,
  XCircle,
  Zap,
  Radio,
  Globe,
  Link,
  Upload,
  Settings,
  Webhook,
  Target,
} from 'lucide-react';
import { config, getWeaveinApiUrl } from '@/src/config';
import { authFetchJson } from '@/lib/authFetch';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
// Use proxy path in production to avoid CORS issues
// The proxy /provider-api/* maps to https://provider-directory-api.onrender.com/api/*
// So /provider-api/import/stats -> https://provider-directory-api.onrender.com/api/import/stats
const getApiBaseUrl = () => {
  if (config.isProduction) {
    // Don't add /api here - the proxy already maps to /api
    return (config.isDesktop ? config.providerApi.baseUrl : '/provider-api');
  }
  // For dev/local, use the full base URL which includes /api
  return config.providerApi.baseUrl;
};

const API_BASE_URL = getApiBaseUrl();

interface Stats {
  total_providers: number;
  active_jobs: number;
  by_source: Record<string, number>;
}

interface ImportJob {
  job_id: string;
  status: 'completed' | 'running' | 'queued' | 'failed' | 'cancelled';
  data_source: string;
  states?: string[];
  limit?: number;
  taxonomy?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  progress?: Record<string, any>;
  results?: {
    added?: number;
    updated?: number;
    failed?: number;
  };
}

// ============================================================================
// INTENT SOURCES SECTION
// ============================================================================
//
// The public-data sources the program-intelligence import reads (CMS, HRSA,
// state filings, uploads), from GET /api/intelligence-import/sources. This used
// to be a hard-coded list of CRM and marketing tools that were never
// connected; every row here now exists. Each organization picks which of these
// feed its own funder search under Settings → Funding & Growth (TRUS-776).

interface IntelligenceSource {
  id: number;
  source_name: string;
  source_type: string;
  source_url?: string | null;
  default_state?: string | null;
  refresh_frequency?: string | null;
  last_refresh_at?: string | null;
  is_active: boolean;
}

export function IntentSourcesSection() {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [sources, setSources] = useState<IntelligenceSource[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    authFetchJson<IntelligenceSource[]>(getWeaveinApiUrl('/api/intelligence-import/sources'))
      .then((rows) => !cancelled && setSources(rows))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, []);

  const active = (sources ?? []).filter((s) => s.is_active).length;

  return (
    <div className="space-y-4">
      <div className="rounded-[10px] border border-v2border bg-surface shadow-v2-1 p-4">
        <div className="flex items-center gap-2 mb-1">
          <Radio className="h-4 w-4 text-ink-500" />
          <h3 className="font-display text-sm font-semibold text-ink">{t('admin.intent_sources_title', 'Intent sources')}</h3>
        </div>
        <p className="text-[13px] text-ink-500">
          {t(
            'admin.intent_sources_desc',
            'Public data the program import reads to find programs and the sponsors that fund them. Each organization chooses which sources feed its own funder search under Settings → Funding & Growth.',
          )}
        </p>
        {sources && (
          <p className="mt-2 text-[12px] text-ink-400">
            {t('admin.intent_sources_count', '{{active}} of {{total}} sources active', { active, total: sources.length })}
          </p>
        )}
      </div>

      {error && (
        <p role="alert" className="rounded-[10px] border border-v2border bg-attention-soft px-3 py-2 text-[13px] text-attention-ink">
          {t('admin.intent_sources_error', 'Could not load sources')}: {error}
        </p>
      )}

      {sources === null && !error && (
        <p className="text-[13px] text-ink-500">{t('admin.loading', 'Loading…')}</p>
      )}

      {sources && sources.length === 0 && (
        <p className="text-[13px] text-ink-500">
          {t('admin.intent_sources_empty', 'No sources are configured yet. Add one from the Program Import tab.')}
        </p>
      )}

      {sources && sources.length > 0 && (
        <ul className="rounded-[10px] border border-v2border bg-surface shadow-v2-1 divide-y divide-v2border-muted">
          {sources.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-[14px] font-medium text-ink">{s.source_name}</p>
                <p className="text-[12px] text-ink-400">
                  {s.source_type}
                  {s.default_state ? ` · ${s.default_state}` : ''}
                  {s.refresh_frequency ? ` · ${s.refresh_frequency}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-3 text-[12px] text-ink-500">
                <span>
                  {s.last_refresh_at
                    ? `${t('admin.intent_sources_last_run', 'Last run')} ${fmt.dateTime(s.last_refresh_at)}`
                    : t('admin.intent_sources_never_run', 'Not run yet')}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    s.is_active ? 'bg-success-soft text-success-ink' : 'bg-paper-2 text-ink-500'
                  }`}
                >
                  {s.is_active ? t('admin.active', 'Active') : t('admin.inactive', 'Inactive')}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ImportManagerDashboard: React.FC = () => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  // State management
  // Tabs: Provider Import | Job History
  const [activeSection, setActiveSection] = useState<'import' | 'jobs'>('import');
  const [stats, setStats] = useState<Stats | null>(null);
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [activeJobs, setActiveJobs] = useState<ImportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<ImportJob | null>(null);

  // Form state
  const [selectedStates, setSelectedStates] = useState<string[]>(['CA']);
  const [limit, setLimit] = useState(1000);
  const [taxonomy, setTaxonomy] = useState('Doula');
  const [enumerationType, setEnumerationType] = useState('');
  const [dataSource, setDataSource] = useState('cms_nppes');

  // US States for selection
  const US_STATES = [
    'CA', 'TX', 'FL', 'NY', 'PA', 'IL', 'OH', 'GA', 'NC', 'MI',
    'NJ', 'VA', 'WA', 'AZ', 'MA', 'TN', 'IN', 'MD', 'MO', 'WI',
    'CO', 'MN', 'SC', 'AL', 'LA', 'KY', 'OR', 'OK', 'CT', 'UT',
    'IA', 'NV', 'AR', 'MS', 'KS', 'NM', 'NE', 'ID', 'WV', 'HI',
    'NH', 'ME', 'RI', 'MT', 'DE', 'SD', 'ND', 'AK', 'VT', 'WY', 'DC'
  ];

  // Common taxonomies. NPPES is searched by taxonomy DESCRIPTION, not code, so
  // these strings have to match the NUCC description exactly. The code beside
  // each is what lands in providers.primary_taxonomy_code.
  const TAXONOMIES = [
    { label: 'Doula', code: '374J00000X', note: 'Perinatal support' },
    { label: 'Midwife', code: '176B00000X', note: 'Perinatal support' },
    { label: 'Lactation Consultant, Non-RN', code: '174N00000X', note: 'Perinatal support' },
    { label: 'Community Health Worker', code: '172V00000X', note: 'Perinatal support' },
    { label: 'Obstetrics & Gynecology', code: '207V00000X' },
    { label: 'Family Medicine', code: '207Q00000X' },
    { label: 'Internal Medicine', code: '207R00000X' },
    { label: 'Pediatrics', code: '208000000X' },
    { label: 'Nurse Practitioner', code: '363L00000X' },
    { label: 'Psychiatry', code: '2084P0800X' },
    { label: 'Emergency Medicine', code: '207P00000X' },
    { label: 'Cardiology', code: '207RC0000X' }
  ];

  // NPI-1 is an individual practitioner, NPI-2 an organisation. Doulas, midwives
  // and lactation consultants are overwhelmingly sole proprietors, so defaulting
  // to organisations only would drop most of them.
  const ENUMERATION_TYPES = [
    { value: '', label: 'Both individuals and organisations' },
    { value: 'NPI-1', label: 'Individuals only (NPI-1)' },
    { value: 'NPI-2', label: 'Organisations only (NPI-2)' }
  ];

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(20);
  const [totalJobs, setTotalJobs] = useState(0);

  // Fetch with automatic retry on transient failures (502/503/504)
  const fetchWithRetry = async (url: string, retries = 2, delayMs = 1500): Promise<Response> => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      const response = await fetch(url);
      if (response.ok || response.status === 404 || response.status < 500) {
        return response;
      }
      // Retry on 5xx
      if (attempt < retries) {
        console.warn(`[ImportManager] ${response.status} on attempt ${attempt + 1}, retrying in ${delayMs}ms...`);
        await new Promise(r => setTimeout(r, delayMs * (attempt + 1)));
      } else {
        return response; // return last failed response
      }
    }
    // unreachable but satisfies TS
    return fetch(url);
  };

  // Fetch stats and jobs
  const fetchData = async () => {
    try {
      setStatsLoading(true);
      // Request up to 200 jobs to show full batch imports
      const response = await fetchWithRetry(`${API_BASE_URL}/import/stats?limit=200`);

      if (response.status === 404) {
        throw new Error(
          'Import Manager API not available yet. ' +
          'The backend is deploying - please wait 5-10 minutes and refresh.'
        );
      }

      if (!response.ok) throw new Error(`Failed to fetch stats: ${response.statusText}`);
      const data = await response.json();

      setStats(data);
      setJobs(data.recent_jobs || []);
      setTotalJobs(data.recent_jobs?.length || 0);
      setActiveJobs(data.recent_jobs?.filter((j: ImportJob) =>
        j.status === 'running' || j.status === 'queued'
      ) || []);
      setError(null);
    } catch (err: any) {
      console.error('Import Manager error:', err);
      const errorMsg = err.message.includes('Failed to fetch')
        ? 'Network error - check if backend is running and CORS is configured'
        : err.message;
      setError(errorMsg);
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  };

  // Auto-refresh every 30 seconds (only when there are active jobs)
  useEffect(() => {
    fetchData();

    // Auto-refresh ONLY while an import is actively running (transient), so an
    // idle page doesn't loop. Metrics live on the Analytics tab.
    const interval = setInterval(() => {
      if (activeJobs.length > 0) {
        fetchData();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [activeJobs.length, activeSection]);

  // Start import
  const startImport = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/import/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data_source: dataSource,
          states: selectedStates,
          limit: parseInt(limit.toString()),
          taxonomy: taxonomy || undefined,
          enumeration_type: enumerationType || undefined
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

      const job = await response.json();
      alert(`Import started! Job ID: ${job.job_id.substring(0, 8)}...`);
      fetchData(); // Refresh
    } catch (err: any) {
      const errorMsg = err.message.includes('Failed to fetch')
        ? 'Network error - check if backend is running and CORS is configured'
        : err.message;
      alert(`Error: ${errorMsg}`);
    }
  };

  // Batch import (all selected states)
  const startBatchImport = async () => {
    if (selectedStates.length === 0) {
      alert('Please select at least one state');
      return;
    }

    const confirmed = window.confirm(
      `Start batch import for ${selectedStates.length} states?\n` +
      `This will create ${selectedStates.length} separate jobs.\n` +
      `Total providers: ~${selectedStates.length * limit}`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/import/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          states: selectedStates,
          limit_per_state: parseInt(limit.toString()),
          taxonomy: taxonomy || undefined
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

      const result = await response.json();
      alert(`Batch import started! ${result.jobs.length} jobs created.`);
      fetchData(); // Refresh
    } catch (err: any) {
      const errorMsg = err.message.includes('Failed to fetch')
        ? 'Network error - check if backend is running and CORS is configured'
        : err.message;
      alert(`Error: ${errorMsg}`);
    }
  };

  // Toggle state selection
  const toggleState = (state: string) => {
    setSelectedStates(prev =>
      prev.includes(state)
        ? prev.filter(s => s !== state)
        : [...prev, state]
    );
  };

  const selectAllStates = () => setSelectedStates([...US_STATES]);
  const selectNone = () => setSelectedStates([]);
  const selectTop10 = () => setSelectedStates(['CA', 'TX', 'FL', 'NY', 'PA', 'IL', 'OH', 'GA', 'NC', 'MI']);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
                <Database className="h-8 w-8 text-teal-600" />
                {t('admin.provider_import_manager', 'Provider Import Manager')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {t('admin.import_and_enrich_healthcare_provider', 'Import and enrich healthcare provider data from multiple sources')}
              </p>
            </div>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden md:inline">{t('admin.refresh', 'Refresh')}</span>
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-red-800 dark:text-red-200">{t('admin.error_loading_data', 'Error loading data')}</p>
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
              <button
                onClick={fetchData}
                className="flex items-center gap-2 text-red-600 hover:text-red-700"
              >
                <RefreshCw className="h-4 w-4" />
                {t('admin.retry', 'Retry')}
              </button>
            </div>
          )}

          {/* Provider import metrics moved to the Analytics tab (single metrics surface). */}
        </div>

        {/* Tabs - AI enrichment, web scraping, social media moved to unified Enrichment tab */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
            <div className="flex w-max min-w-full">
              <Tab
                label={t('admin.import_data', 'Provider Import')}
                icon={<Download className="h-4 w-4" />}
                active={activeSection === 'import'}
                onClick={() => setActiveSection('import')}
              />
              <Tab
                label={t('admin.job_history', 'Job History')}
                icon={<FileText className="h-4 w-4" />}
                active={activeSection === 'jobs'}
                onClick={() => setActiveSection('jobs')}
              />
            </div>
          </div>
        </div>

        {/* Import Section */}
        {activeSection === 'import' && (
          <div className="w-full">
            {/* Import Form */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Download className="h-5 w-5 text-teal-600" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('admin.import_provider_data', 'Import Provider Data')}</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {t('admin.import_healthcare_providers_from_national', 'Import healthcare providers from national registries')}
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.data_source', 'Data Source')}
                  </label>
                  <select
                    value={dataSource}
                    onChange={(e) => setDataSource(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="cms_nppes">CMS NPPES (National Provider Registry)</option>
                    <option value="cms_care_compare">CMS Care Compare (Hospitals)</option>
                    <option value="nursing_homes">CMS Nursing Homes</option>
                    <option value="hrsa">{t('admin.hrsa_fqhcs', 'HRSA (FQHCs)')}</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('admin.nppes_7m_providers_care_compare', 'NPPES: 7M+ providers | Care Compare: Hospitals | Nursing Homes: 15K+ facilities | HRSA: Community Health Centers')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.taxonomy_specialty', 'Taxonomy (Specialty)')}
                  </label>
                  <select
                    value={taxonomy}
                    onChange={(e) => setTaxonomy(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">{t('admin.all_specialties', 'All Specialties')}</option>
                    {TAXONOMIES.map(tax => (
                      <option key={tax.code} value={tax.label}>
                        {tax.label} ({tax.code}){tax.note ? ` \u2014 ${tax.note}` : ''}
                      </option>
                    ))}
                  </select>
                  {!taxonomy && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      {t('admin.taxonomy_required_note',
                         'Leaving this blank loads Family Medicine providers only \u2014 pick a specialty to load anything else.')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.provider_kind', 'Provider Kind')}
                  </label>
                  <select
                    value={enumerationType}
                    onChange={(e) => setEnumerationType(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    {ENUMERATION_TYPES.map(opt => (
                      <option key={opt.value || 'both'} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('admin.provider_kind_note',
                       'Doulas and midwives are mostly sole proprietors, so \u201cboth\u201d is the safe default.')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.limit_per_state', 'Limit per State')}
                  </label>
                  <input
                    type="number"
                    value={limit}
                    onChange={(e) => setLimit(parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    min="10"
                    max="10000"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      States ({selectedStates.length} selected)
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={selectTop10}
                        className="text-xs text-teal-600 hover:text-teal-700"
                      >
                        {t('admin.top_10', 'Top 10')}
                      </button>
                      <button
                        onClick={selectAllStates}
                        className="text-xs text-teal-600 hover:text-teal-700"
                      >
                        {t('admin.all', 'All')}
                      </button>
                      <button
                        onClick={selectNone}
                        className="text-xs text-gray-600 hover:text-gray-700"
                      >
                        {t('admin.none', 'None')}
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-2 max-h-40 overflow-y-auto p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    {US_STATES.map(state => (
                      <button
                        key={state}
                        onClick={() => toggleState(state)}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                          selectedStates.includes(state)
                            ? 'bg-teal-600 text-white'
                            : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                        }`}
                      >
                        {state}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={startImport}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all font-medium"
                  >
                    <Play className="h-4 w-4" />
                    {t('admin.start_import', 'Start Import')}
                  </button>
                  <button
                    onClick={startBatchImport}
                    disabled={selectedStates.length === 0}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                  >
                    <Zap className="h-4 w-4" />
                    {t('admin.batch_import', 'Batch Import')}
                  </button>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                    {t('admin.estimated_import_time', 'Estimated Import Time:')}
                  </p>
                  <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                    <li>• 1,000 providers: ~5-10 minutes</li>
                    <li>• 5,000 providers: ~20-30 minutes</li>
                    <li>• Batch import: {selectedStates.length} states = ~{Math.ceil(selectedStates.length * 8)} minutes</li>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Job History Section */}
        {activeSection === 'jobs' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-gray-600" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('admin.import_job_history', 'Import Job History')}</h3>
                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                  {totalJobs} jobs
                </span>
              </div>
              {/* Progress summary for active jobs */}
              {activeJobs.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <Activity className="h-4 w-4 text-blue-600 animate-pulse" />
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    {activeJobs.length} active job{activeJobs.length > 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {t('admin.track_and_monitor_import_jobs', 'Track and monitor import jobs')}
            </p>

            {jobs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('admin.no_import_jobs_yet', 'No import jobs yet')}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {t('admin.start_your_first_import_from', 'Start your first import from the "Import Data" tab')}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.job_id', 'Job ID')}</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.source', 'Source')}</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.states', 'States')}</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.status', 'Status')}</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.progress', 'Progress')}</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.results', 'Results')}</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.created', 'Created')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs
                        .slice((currentPage - 1) * jobsPerPage, currentPage * jobsPerPage)
                        .map(job => (
                        <tr key={job.job_id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="py-3 px-4 font-mono text-xs text-gray-600 dark:text-gray-400">
                            {job.job_id.substring(0, 8)}...
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200">
                              {job.data_source?.toUpperCase().replace('_', ' ')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {job.states?.join(', ') || 'All'}
                          </td>
                          <td className="py-3 px-4">
                            <StatusBadge status={job.status} />
                          </td>
                          <td className="py-3 px-4">
                            {/* Progress bar for running jobs */}
                            {(job.status === 'running' || job.status === 'queued') && job.progress ? (
                              <div className="w-24">
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="text-gray-600 dark:text-gray-400">
                                    {job.progress.processed || 0}
                                  </span>
                                  <span className="font-medium text-teal-600">
                                    {job.progress.percent || 0}%
                                  </span>
                                </div>
                                <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-teal-500 transition-all duration-500"
                                    style={{ width: `${job.progress.percent || 0}%` }}
                                  />
                                </div>
                              </div>
                            ) : job.status === 'completed' ? (
                              <span className="text-xs text-green-600 dark:text-green-400">100%</span>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-2">
                              {job.results?.added !== undefined && (
                                <span className="text-xs text-green-600 dark:text-green-400">
                                  +{job.results.added}
                                </span>
                              )}
                              {job.results?.updated !== undefined && job.results.updated > 0 && (
                                <span className="text-xs text-blue-600 dark:text-blue-400">
                                  ~{job.results.updated}
                                </span>
                              )}
                              {job.results?.failed !== undefined && job.results.failed > 0 && (
                                <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                                  !{job.results.failed}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-xs text-gray-600 dark:text-gray-400">
                            {fmt.dateTime(job.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalJobs > jobsPerPage && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Showing {((currentPage - 1) * jobsPerPage) + 1} to {Math.min(currentPage * jobsPerPage, totalJobs)} of {totalJobs} jobs
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('admin.previous', 'Previous')}
                      </button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.ceil(totalJobs / jobsPerPage) }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first, last, current, and adjacent pages
                            const totalPages = Math.ceil(totalJobs / jobsPerPage);
                            return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                          })
                          .map((page, idx, arr) => (
                            <React.Fragment key={page}>
                              {idx > 0 && arr[idx - 1] !== page - 1 && (
                                <span className="px-2 text-gray-400">...</span>
                              )}
                              <button
                                onClick={() => setCurrentPage(page)}
                                className={`w-8 h-8 text-sm rounded-lg ${
                                  currentPage === page
                                    ? 'bg-teal-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          ))}
                      </div>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalJobs / jobsPerPage), p + 1))}
                        disabled={currentPage >= Math.ceil(totalJobs / jobsPerPage)}
                        className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('admin.next', 'Next')}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components

interface StatCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: 'teal' | 'blue' | 'purple' | 'green';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => {
  const colorClasses = {
    teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">{label}</p>
        </div>
      </div>
    </div>
  );
};

interface TabProps {
  label: string;
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}

const Tab: React.FC<TabProps> = ({ label, active, onClick, icon }) => (
  <button
    onClick={onClick}
    className={`
      px-3 sm:px-6 py-3 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center gap-1 sm:gap-2 flex-shrink-0
      ${active
        ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50 dark:bg-teal-900/20'
        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
      }
    `}
  >
    {icon}
    {label}
  </button>
);

interface StatusBadgeProps {
  status: 'completed' | 'running' | 'queued' | 'failed' | 'cancelled';
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const configs = {
    completed: {
      bg: 'bg-green-100 dark:bg-green-900/30',
      text: 'text-green-800 dark:text-green-200',
      icon: <CheckCircle className="h-3 w-3" />
    },
    running: {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-800 dark:text-blue-200',
      icon: <Activity className="h-3 w-3" />
    },
    queued: {
      bg: 'bg-amber-100 dark:bg-amber-900/30',
      text: 'text-amber-800 dark:text-amber-200',
      icon: <Clock className="h-3 w-3" />
    },
    failed: {
      bg: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-800 dark:text-red-200',
      icon: <XCircle className="h-3 w-3" />
    },
    cancelled: {
      bg: 'bg-gray-100 dark:bg-gray-700',
      text: 'text-gray-800 dark:text-gray-200',
      icon: <XCircle className="h-3 w-3" />
    }
  };

  const config = configs[status];

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.icon}
      {status}
    </span>
  );
};

export default ImportManagerDashboard;
